import React, { useState, useEffect } from 'react';
import { X, Settings, Phone, Clock, FileText, RefreshCw } from 'lucide-react';
import request from '../../api/request';

interface OrderAnalysisSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSettingsChange?: (enabled: boolean) => void;
}

interface SettingsData {
    orderAnalysisEnabled: boolean;
    csPhone: string;  // 统一客服电话
    templateName: string;
    templateCooldownHours: number;
    orderAnalysisSchedule: string;
    loopAnalysisMode: boolean;
}

const OrderAnalysisSettingsModal: React.FC<OrderAnalysisSettingsModalProps> = ({ isOpen, onClose, onSettingsChange }) => {
    const [settings, setSettings] = useState<SettingsData>({
        orderAnalysisEnabled: true,
        csPhone: '',
        templateName: 'kk',
        templateCooldownHours: 2,
        orderAnalysisSchedule: '9,11,13,15,17',
        loopAnalysisMode: false,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (isOpen) {
            loadSettings();
        }
    }, [isOpen]);

    const loadSettings = async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await request.get('/api/settings');
            setSettings({
                orderAnalysisEnabled: response.data.orderAnalysisEnabled ?? true,
                csPhone: response.data.csPhone || '',
                templateName: response.data.templateName || 'kk',
                templateCooldownHours: response.data.templateCooldownHours || 2,
                orderAnalysisSchedule: response.data.orderAnalysisSchedule || '9,11,13,15,17',
                loopAnalysisMode: response.data.loopAnalysisMode ?? false,
            });
        } catch (err) {
            setError('加载设置失败');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        setError('');
        setSuccess('');
        try {
            await request.put('/api/settings', settings);
            setSuccess('保存成功！');
            onSettingsChange?.(settings.orderAnalysisEnabled);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('保存失败');
            console.error(err);
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Settings className="w-5 h-5 text-primary" />
                        客服跟进设置
                    </h2>
                    <button onClick={onClose} className="p-1 hover:bg-muted rounded-full transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4 space-y-4">
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                    ) : (
                        <>
                            {/* 开关 */}
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium">启用客服跟进</label>
                                <button
                                    onClick={() => setSettings({ ...settings, orderAnalysisEnabled: !settings.orderAnalysisEnabled })}
                                    className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${settings.orderAnalysisEnabled ? 'bg-primary' : 'bg-muted'}`}
                                >
                                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${settings.orderAnalysisEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                                </button>
                            </div>

                            {/* 阿克拉客服电话 */}
                            <div>
                                <label className="text-sm font-medium flex items-center gap-1 mb-1">
                                    <Phone className="w-4 h-4" />
                                    客服电话
                                </label>
                                <input
                                    type="text"
                                    value={settings.csPhone}
                                    onChange={(e) => setSettings({ ...settings, csPhone: e.target.value })}
                                    placeholder="例如: 233XXXXXXXXX"
                                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:outline-none"
                                />
                            </div>

                            {/* 模板消息名称 */}
                            <div>
                                <label className="text-sm font-medium flex items-center gap-1 mb-1">
                                    <FileText className="w-4 h-4" />
                                    WhatsApp 模板名称
                                </label>
                                <input
                                    type="text"
                                    value={settings.templateName}
                                    onChange={(e) => setSettings({ ...settings, templateName: e.target.value })}
                                    placeholder="例如: kk"
                                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:outline-none"
                                />
                            </div>

                            {/* 模板冷却期 */}
                            <div>
                                <label className="text-sm font-medium flex items-center gap-1 mb-1">
                                    <Clock className="w-4 h-4" />
                                    模板消息冷却期（小时）
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="48"
                                    value={settings.templateCooldownHours}
                                    onChange={(e) => setSettings({ ...settings, templateCooldownHours: parseInt(e.target.value) || 2 })}
                                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:outline-none"
                                />
                            </div>

                            {/* 运行时间 */}
                            <div>
                                <label className="text-sm font-medium flex items-center gap-1 mb-1">
                                    <Clock className="w-4 h-4" />
                                    定时运行时间（UTC小时，逗号分隔）
                                </label>
                                <input
                                    type="text"
                                    value={settings.orderAnalysisSchedule}
                                    onChange={(e) => setSettings({ ...settings, orderAnalysisSchedule: e.target.value })}
                                    placeholder="例如: 9,11,13,15,17"
                                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:outline-none"
                                    disabled={settings.loopAnalysisMode}
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    阿克拉时间与 UTC 相同，周一至周六运行
                                </p>
                            </div>

                            {/* 循环分析模式开关 */}
                            <div className="flex items-center justify-between p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <RefreshCw className={`w-4 h-4 ${settings.loopAnalysisMode ? 'animate-spin text-destructive' : 'text-muted-foreground'}`} />
                                    <div>
                                        <label className="text-sm font-medium">循环分析模式（测试用）</label>
                                        <p className="text-xs text-muted-foreground">开启后立即循环执行分析，定时任务失效</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSettings({ ...settings, loopAnalysisMode: !settings.loopAnalysisMode })}
                                    className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${settings.loopAnalysisMode ? 'bg-destructive' : 'bg-muted'}`}
                                >
                                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${settings.loopAnalysisMode ? 'translate-x-6' : 'translate-x-1'}`} />
                                </button>
                            </div>

                            {/* 错误和成功提示 */}
                            {error && (
                                <div className="text-sm text-destructive bg-destructive/10 rounded-md p-2">
                                    {error}
                                </div>
                            )}
                            {success && (
                                <div className="text-sm text-accent bg-accent/10 rounded-md p-2">
                                    {success}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 p-4 border-t border-border">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm text-muted-foreground hover:bg-muted rounded-md transition-colors"
                    >
                        取消
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isLoading || isSaving}
                        className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                        {isSaving ? '保存中...' : '保存'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OrderAnalysisSettingsModal;
